import { useCallback } from 'react'

import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { OrgTransaction } from '~/types'
import { InfiniteTransactionsResponse } from '~/types/query-types'

export const useRejectTransaction = (): {
  rejectTransaction: (
    orgId: string,
    transaction: OrgTransaction,
  ) => Promise<void>
  isProcessing: boolean
} => {
  const { isProcessing, serverDeleteRequest } = useServerRequests()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const rejectTransaction = useCallback(
    async (orgId: string, transaction: OrgTransaction) => {
      const result = await serverDeleteRequest({
        path: `/api/smart_accounts/transactions/${transaction.id}`,
        errorMessage: 'Failed to cancel transaction',
      })

      if (result) {
        queryClient.setQueriesData(
          {
            queryKey: [QUERY_KEYS.TRANSACTIONS],
            predicate: (query) => {
              return (
                Array.isArray(query.queryKey) &&
                (query.queryKey[1]?.smartAccountId ===
                  transaction.smartAccountId ||
                  query.queryKey[1]?.orgId === orgId)
              )
            },
          },
          (
            oldData:
              InfiniteData<InfiniteTransactionsResponse, unknown> | undefined,
          ) => {
            if (!oldData) return oldData

            const updatedTransactions = oldData.pages.map((page) => {
              return {
                transactions: page.transactions.filter(
                  (t) => t.id !== transaction.id,
                ),
                nextPage: page.nextPage,
              }
            })

            return {
              ...oldData,
              pages: updatedTransactions.map(({ transactions, nextPage }) => ({
                transactions,
                nextPage,
              })),
            }
          },
        )

        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
        })

        toast.success('Transaction canceled', {
          id: transaction.id,
          description: <TransactionTitle transaction={transaction} unstyled />,
        })
      }
    },
    [queryClient, serverDeleteRequest, activeOrgId],
  )

  return {
    rejectTransaction,
    isProcessing,
  }
}
